/**
 * Test page for comparing listAreas2 vs listAreas3 Performance
 * Access at: /admin/test-areas3
 *
 * Focuses on the four main query patterns:
 * 1. useAreas Hook / Store Admin
 * 2. R0002 Area Selector
 * 3. R0003 Store Dropdown
 * 4. R0003 Training
 *
 * Each test includes performance timing to compare response times between
 * listAreas2 and listAreas3 implementations.
 */

import React, { useState } from 'react';
import { Box, Button, Typography, Paper, Alert, CircularProgress } from '@mui/material';
import { useUser } from 'components/RDAuthContext';
import testListAreas3 from '../../../utils/testListAreas3';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';

function TestListAreas3() {
  const { user } = useUser();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentTest, setCurrentTest] = useState('');
  
  const runAllTests = async () => {
    setLoading(true);
    setResults(null);
    try {
      const username = user?.username || 'testUser';
      const role = user?.role || 'Admin';
      const testResults = await testListAreas3.runAllTests(username, role);
      setResults(testResults);
    } catch (error) {
      console.error('Test failed:', error);
      setResults({ error: error.message });
    } finally {
      setLoading(false);
      setCurrentTest('');
    }
  };
  
  const runQuickTest = async () => {
    setLoading(true);
    setResults(null);
    setCurrentTest('Quick Test - Store Admin');
    try {
      const result = await testListAreas3.quickTest();
      setResults([result]);
    } catch (error) {
      console.error('Test failed:', error);
      setResults({ error: error.message });
    } finally {
      setLoading(false);
      setCurrentTest('');
    }
  };
  
  
  const runSingleTest = async (testCase) => {
    setLoading(true);
    setCurrentTest(testCase.name);
    try {
      const variables = { ...testCase.variables };
      // Replace placeholders
      if (variables.user === '[username]' || variables.user === '[user]') {
        variables.user = user?.username || 'testUser';
      }
      if (variables.role === '[role]') {
        variables.role = user?.role || 'Admin';
      }
      
      const result = await testListAreas3.runSingleTest(variables, testCase.name);
      setResults([result]);
    } catch (error) {
      console.error('Test failed:', error);
      setResults({ error: error.message });
    } finally {
      setLoading(false);
      setCurrentTest('');
    }
  };
  
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Test listAreas3 Compatibility
        </Typography>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          This page compares the performance of listAreas2 (current) vs listAreas3 (optimized) queries.
          <br />
          • Performance metrics are shown for each query
          <br />
          • Full response objects are logged to the console
          <br />
          • We expect the data to differ between the two implementations
          <br />
          Check the browser console for detailed output.
        </Alert>
        
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            onClick={runAllTests}
            disabled={loading}
          >
            Run All Tests
          </Button>
          
          <Button 
            variant="outlined" 
            onClick={runQuickTest}
            disabled={loading}
          >
            Quick Test (Store Admin)
          </Button>
          
        </Box>
        
        <Typography variant="h6" gutterBottom>
          Individual Test Cases:
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          {testListAreas3.TEST_CASES.map((testCase, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => runSingleTest(testCase)}
                disabled={loading}
              >
                Test
              </Button>
              <Typography variant="body2">
                <strong>{testCase.name}:</strong> {JSON.stringify(testCase.variables)}
              </Typography>
              {currentTest === testCase.name && <CircularProgress size={20} />}
            </Box>
          ))}
        </Box>
        
        {loading && !currentTest && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography>Running tests...</Typography>
          </Box>
        )}
        
        {results && !results.error && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Results:
            </Typography>
            
            {Array.isArray(results) && results.map((result, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Typography variant="subtitle1">
                  {result.match ? '✅' : '❌'} {result.testName}
                </Typography>
                
                {result.error ? (
                  <Alert severity="error">{result.error}</Alert>
                ) : (
                  <>
                    <Typography variant="body2">
                      Variables: {JSON.stringify(result.variables)}
                    </Typography>
                    
                    {result.stats?.performance && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          <strong>Performance:</strong> listAreas2: {result.stats.performance.listAreas2} |
                          listAreas3: {result.stats.performance.listAreas3} |
                          <strong>{result.stats.performance.speedup}</strong>
                        </Typography>
                      </Alert>
                    )}
                    
                    
                    {result.stats && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          listAreas2: {result.stats.listAreas2.areaCount} areas, {result.stats.listAreas2.totalStores} stores ({result.stats.listAreas2.responseTime}ms)
                        </Typography>
                        <Typography variant="body2">
                          listAreas3: {result.stats.listAreas3.areaCount} areas, {result.stats.listAreas3.totalStores} stores ({result.stats.listAreas3.responseTime}ms)
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            ))}
          </Paper>
        )}
        
        {results?.error && (
          <Alert severity="error">
            Test failed: {results.error}
          </Alert>
        )}
        
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Open the browser Developer Console (F12) to see detailed comparison results.
          </Typography>
        </Box>
      </Box>
    </DashboardLayout>
  );
}

export default TestListAreas3;